import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../../typography/button/button';
import { Field } from '../../forms/components/field';
import { useValidation } from '@/hooks/use-validation';
import { Arrow } from './components/svg';
import { PropsType } from './bank-open-savings-form';
import { GenericValidationError } from './components/generic-validation-error';

type Props = PropsType & {
    login: () => void;
    hasStepZeroFeature: boolean;
};

export const StepOne = ({
    labels,
    handleChange,
    hasStepZeroFeature,
    values,
    step,
    setStep,
    login,
    user,
    scrollToFirstValidationErrorElement,
}: Props) => {
    const { getValidatorByInputName } = useValidation();

    const [disabled, setDisabled] = useState(true);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const validationState = useMemo(() => {
        const requiredFields = {
            name: !!values.name,
            streetAddress: !!values.streetAddress,
            zipCode: !!values.zipCode,
            city: !!values.city,
            mobile: getValidatorByInputName('phone')?.(values.phoneMobile),
            email: getValidatorByInputName('email')?.(values.contactEmail),
        };

        return {
            ...requiredFields,
            allFieldsValid: Object.values(requiredFields).every(Boolean),
        };
    }, [values]);

    useEffect(() => {
        if (user?.isAuthenticated) {
            setDisabled(false);
        }
    }, [user]);

    const onSubmit = () => {
        if (validationState.allFieldsValid) {
            setStep(step + 1);
            return;
        }

        setShowValidationErrors(true);
        scrollToFirstValidationErrorElement();
    };

    return (
        <>
            <div className="r-mb-4 r-mt-4 r-space-y-6">
                {disabled && (
                    <Button type="primary" fullWidth onClick={() => login()}>
                        <div className="r-flex r-space-x-1">
                            <p>{labels.formLoginButtonText}</p>
                            <img src="/assets/icons/bankid-se.svg" alt="bankid-icon" />
                        </div>
                    </Button>
                )}
            </div>
            <div
                className={clsx('r-space-y-4', {
                    'r-pointer-events-none r-opacity-50': disabled,
                })}
            >
                <p>{labels.formStepOneDescription}</p>

                {showValidationErrors && !validationState.allFieldsValid && <GenericValidationError />}

                <Field disabled value={values.name} onChange={v => handleChange(v, 'name')} label={`${labels.name} *`} />
                <Field disabled value={values.streetAddress} onChange={v => handleChange(v, 'streetAddress')} label={`${labels.streetAddress} *`} />
                <div className="max-md:r-space-y-4 md:r-flex md:r-gap-4">
                    <div className="r-flex-shrink r-flex-grow" style={{ flexBasis: '30%' }}>
                        <Field disabled value={values.zipCode} onChange={v => handleChange(v, 'zipCode')} label={`${labels.zipCode} *`} />
                    </div>
                    <div className="r-flex-shrink r-flex-grow" style={{ flexBasis: '60%' }}>
                        <Field disabled value={values.city} onChange={v => handleChange(v, 'city')} label={`${labels.city} *`} />
                    </div>
                </div>
                <div>
                    <Field
                        grouped="top"
                        value={values.phoneDay}
                        onChange={v => handleChange(v, 'phoneDay')}
                        infoText={labels.phoneDayInfoText}
                        label={labels.phoneDay}
                        validation={getValidatorByInputName('phone')}
                    />
                    <Field
                        grouped="middle"
                        value={values.phoneMobile}
                        onChange={v => handleChange(v, 'phoneMobile')}
                        infoText={labels.phoneMobileInfoText}
                        label={`${labels.phoneMobile} *`}
                        validation={getValidatorByInputName('phone')}
                        showRequiredError={showValidationErrors && !validationState.mobile}
                    />
                    <Field
                        grouped="bottom"
                        value={values.contactEmail}
                        onChange={v => handleChange(v, 'contactEmail')}
                        infoText={labels.contactEmailInfoText}
                        label={`${labels.contactEmail} *`}
                        validation={getValidatorByInputName('email')}
                        showRequiredError={showValidationErrors && !validationState.email}
                    />
                </div>
                {hasStepZeroFeature ? (
                    <div className="r-flex r-justify-center r-gap-4 md:r-gap-8">
                        <Button type="secondary" fullWidth onClick={() => setStep(step - 1)}>
                            <div className="r-flex r-gap-2">
                                <Arrow className="r-w-4 r-rotate-180" />
                                <p>{labels.back}</p>
                            </div>
                        </Button>
                        <Button ariaDisabled={!validationState.allFieldsValid} fullWidth onClick={onSubmit}>
                            <div className="r-flex r-gap-2">
                                <p>
                                    {labels.continue} {step + 1}
                                </p>
                                <Arrow className="r-w-4" />
                            </div>
                        </Button>
                    </div>
                ) : (
                    <Button onClick={onSubmit} fullWidth ariaDisabled={!validationState.allFieldsValid} type="tertiary">
                        <div className="r-flex r-items-center r-gap-2">
                            <p>
                                {labels.continue} {step + 1}
                            </p>
                            <Arrow className="r-w-4" />
                        </div>
                    </Button>
                )}
            </div>
        </>
    );
};
